export default function useStepValidation({
  formData,
  setErrors,
  businessData,
  businessUpdateData,
  venueData,
  vendorProfileData,
  cityFromUrl,
}) {
  const validateStep1 = () => {
    const newErrors = {};

    if (!formData.businessType)
      newErrors.businessType = "Please select vendor or venue";

    if (!formData.city?.trim()) newErrors.city = "City is required";

    if (!formData.name?.trim()) newErrors.name = "Name is required";

    if (!formData.email?.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Please enter a valid email";

    const cleanedPhone = formData.phone.replace(/\D/g, "");

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^(0\d{10}|91\d{10}|\d{10})$/.test(cleanedPhone)) {
      newErrors.phone = "Please enter a valid phone number";
    }
    if (formData.price_min === "" || formData.price_min == null)
      if (formData.businessType === "venue")
        newErrors.price_min = "Minimum price is required";
      else if (formData.businessType === "vendor")
        newErrors.price_min = "Starting package price is required";

    if (formData.businessType === "venue") {
      if (formData.price_max === "" || formData.price_max == null)
        newErrors.price_max = "Maximum price is required";
    }

    if (formData.businessType === "venue") {
      if (formData.price_max === "" || formData.price_max == null)
        newErrors.price_max = "Maximum price is required";

      const minNum = Number(formData.price_min);
      const maxNum = Number(formData.price_max);

      if (
        !newErrors.price_min &&
        !newErrors.price_max &&
        !Number.isNaN(minNum) &&
        !Number.isNaN(maxNum) &&
        minNum > maxNum
      )
        newErrors.price_max = "Maximum price cannot be less than minimum price";
      if (
        !newErrors.price_min &&
        !newErrors.price_max &&
        !Number.isNaN(minNum) &&
        !Number.isNaN(maxNum) &&
        minNum === maxNum
      )
        newErrors.price_max = "Maximum price cannot be equal to minimum price";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getInitialStep1Data = () => {
    const biz = businessData ?? businessUpdateData?.getBusinessData;

    if (!biz) {
      return {
        businessType: "",
        city: cityFromUrl || "",
        name: "",
        email: "",
        phone: "",
        price_min: "",
        price_max: "",
      };
    }

    return {
      businessType:
        (biz.business_type ?? biz.businessType ?? "") === "vendor"
          ? "vendor"
          : "venue",
      city: (biz.city ?? "").toString().trim(),
      name: (biz.business_name ?? biz.name ?? "").toString().trim(),
      email: (biz.email ?? "").toString().trim(),
      phone: (biz.phone ?? "").toString().trim(),
      price_min:
        biz.price_min != null && biz.price_min !== ""
          ? String(biz.price_min)
          : "",
      price_max:
        biz.price_max != null && biz.price_max !== ""
          ? String(biz.price_max)
          : "",
    };
  };

  const isStep1DataChanged = () => {
    const initial = getInitialStep1Data();
    const s = (v) => (v == null || v === undefined ? "" : String(v).trim());

    return (
      s(formData.businessType) !== s(initial.businessType) ||
      s(formData.city) !== s(initial.city) ||
      s(formData.name) !== s(initial.name) ||
      s(formData.email) !== s(initial.email) ||
      s(formData.phone) !== s(initial.phone) ||
      s(formData.price_min) !== s(initial.price_min) ||
      s(formData.price_max) !== s(initial.price_max)
    );
  };

  const validateStep2Venue = () => {
    const validTypes = ["banquet", "lawn", "hotel", "resort"];
    const newErrors = {};

    if (!validTypes.includes(formData.venue_type))
      newErrors.venue_type =
        "Please select venue type (Banquet, Lawn, Hotel, or Resort)";

    if (!formData.capacity_min)
      newErrors.capacity_min = "Minimum capacity is required";

    if (!formData.capacity_max)
      newErrors.capacity_max = "Maximum capacity is required";

    const capMin = Number(formData.capacity_min);
    const capMax = Number(formData.capacity_max);

    if (!newErrors.capacity_min && !newErrors.capacity_max && capMin > capMax)
      newErrors.capacity_max =
        "Maximum capacity cannot be less than minimum capacity";
    if (!newErrors.capacity_min && !newErrors.capacity_max && capMin === capMax)
      newErrors.capacity_max =
        "Maximum capacity cannot be equal to minimum capacity";
    if (!formData.number_of_halls)
      newErrors.number_of_halls = "Number of halls is required";

    if (!formData.full_address?.trim())
      newErrors.full_address = "Full address is required";

    if (!formData.city?.trim()) newErrors.city = "City is required";

    if (!formData.state?.trim()) newErrors.state = "State is required";

    if (!formData.pincode?.trim()) newErrors.pincode = "Pincode is required";
    else if (!/^\d{6}$/.test(formData.pincode))
      newErrors.pincode = "Please enter a valid 6-digit pincode";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2Vendor = () => {
    const newErrors = {};

    if (!formData.willing_to_travel)
      newErrors.willing_to_travel = "Please select if willing to travel";

    if (!formData.vendor_type?.trim())
      newErrors.vendor_type = "Vendor type is required";

    if (
      formData.years_of_experience === "" ||
      formData.years_of_experience == null
    )
      newErrors.years_of_experience = "Years of experience is required";
    else if (Number(formData.years_of_experience) < 0)
      newErrors.years_of_experience = "Enter a valid number";

    if (formData.service_radius_km === "" || formData.service_radius_km == null)
      newErrors.service_radius_km = "Service radius (KM) is required";
    else if (Number(formData.service_radius_km) < 0)
      newErrors.service_radius_km = "Enter a valid number";

    if (!formData.city?.trim()) newErrors.city = "City is required";
    if (!formData.state?.trim()) newErrors.state = "State is required";

    if (formData.pincode?.trim() && !/^\d{6}$/.test(formData.pincode))
      newErrors.pincode = "Please enter a valid 6-digit pincode";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isStep2VenueDataChanged = () => {
    const v = venueData?.getVenueData;
    if (!v) return true;

    return (
      formData.venue_type !== (v.venue_type ?? "") ||
      formData.capacity_min !== String(v.capacity_min ?? "") ||
      formData.capacity_max !== String(v.capacity_max ?? "") ||
      formData.area_sqft !== String(v.area_sqft ?? "") ||
      formData.number_of_halls !== String(v.number_of_halls ?? "") ||
      formData.full_address !== (v.full_address ?? "") ||
      formData.city !== (v.city ?? "") ||
      formData.state !== (v.state ?? "") ||
      formData.pincode !== (v.pincode ?? "")
    );
  };

  const isStep2VendorDataChanged = () => {
    const v = vendorProfileData?.getProfileData ?? venueData?.getVenueData;
    if (!v) return true;
    const data = v?.vendor ?? v;
    return (
      formData.willing_to_travel !== (data.willing_to_travel ?? "") ||
      formData.vendor_type !== (data.vendor_type ?? "") ||
      formData.years_of_experience !== String(data.years_of_experience ?? "") ||
      formData.service_radius_km !== String(data.service_radius_km ?? "") ||
      formData.full_address !== (data.full_address ?? "") ||
      formData.city !== (data.city ?? "") ||
      formData.state !== (data.state ?? "") ||
      formData.pincode !== (data.pincode ?? "")
    );
  };

  const validateStep3Venue = () => {
    const newErrors = {};

    if (!formData.description?.trim())
      newErrors.description = "Description is required";
    else if (formData.description?.trim().length < 100)
      newErrors.description =
        "Description must be at least 100 characters long";

    if (!formData.amenities?.trim())
      newErrors.amenities =
        formData.businessType === "vendor" ? "Offerings are required" : "Amenities are required";

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const isStep3DataChanged = () => {
    const v = venueData?.getVenueData;
    if (!v) return true;
    return (
      formData.description !== (v.description ?? "") ||
      formData.amenities !== (v.amenities ?? "")
    );
  };

  const isStep3VendorDataChanged = () => {
    const v = vendorProfileData?.getProfileData ?? venueData?.getVenueData;
    if (!v) return true;
    const data = v?.vendor ?? v;
    return (
      formData.description !== (data.description ?? "") ||
      formData.amenities !== (data.amenities ?? "")
    );
  };

  return {
    validateStep1,
    isStep1DataChanged,
    validateStep2Venue,
    isStep2VenueDataChanged,
    validateStep2Vendor,
    isStep2VendorDataChanged,
    validateStep3Venue,
    isStep3DataChanged,
    isStep3VendorDataChanged,
  };
}
